import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowRight, Mail, User } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { getApiErrorMessage } from '@/lib/api-client'
import { useRegister } from '../hooks'
import { registerSchema, type RegisterValues } from '../schemas'
import { AuthLayout } from './auth-layout'
import { AuthField } from './auth-field'
import { zodResolver } from './zod-resolver'

export function RegisterPage() {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const registerMutation = useRegister()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true)
    try {
      await registerMutation.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || undefined,
        password: values.password,
      })
      toast.success('Account created', {
        description: 'Please verify your email to complete registration.',
      })
      navigate(`${appRoutes.auth.verificationPending}?email=${encodeURIComponent(values.email)}`, {
        replace: true,
      })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to create account'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <AuthLayout
      title="Create your account"
      description="Join the premier automotive marketplace today."
      footer={
        <>
          Already have an account?{' '}
          <Link to={appRoutes.auth.login} className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <AuthField
                label="First Name"
                icon={<User />}
                placeholder="John"
                autoComplete="given-name"
                error={errors.firstName}
                {...field}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <AuthField
                label="Last Name"
                placeholder="Doe"
                autoComplete="family-name"
                error={errors.lastName}
                {...field}
              />
            )}
          />
        </div>

        <AuthField
          label="Email Address"
          icon={<Mail />}
          placeholder="you@example.com"
          type="email"
          autoComplete="email"
          error={errors.email}
          {...register('email')}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <AuthField
              label="Phone Number"
              placeholder="+1 555 000 0000"
              autoComplete="tel"
              error={errors.phone}
              {...field}
            />
          )}
        />

        <AuthField
          label="Password"
          placeholder="At least 8 characters"
          type="password"
          autoComplete="new-password"
          error={errors.password}
          {...register('password')}
        />

        <AuthField
          label="Confirm Password"
          placeholder="Re-enter your password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword}
          {...register('confirmPassword')}
        />

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? <ArrowRight className="size-4 animate-spin" /> : null}
          {submitting ? 'Creating account...' : 'Create Account'}
          {!submitting ? <ArrowRight className="size-4" /> : null}
        </Button>
      </form>
    </AuthLayout>
  )
}